import { useEffect, useState } from "react";
import { defaultOrbParams, isWebglOrbVariant } from "@/types/ai-assistant-orb";
import type { AssistantOrbProps } from "./assistant-orb.types";
import { AssistantCanvas2dOrb } from "./assistant-orb-canvas2d";
import { AssistantOrbWebgl } from "./webgl/assistant-orb-webgl";
import {
	isWebglStageAvailable,
	subscribeToWebglAvailability,
	webglShaderKindFor,
} from "./webgl/orb-webgl-stage";

/**
 * Logo Jenna sebagai orb animasi. Dispatcher tipis: varian Canvas 2D
 * (particle-sphere/wireframe-mesh/glow-ring, T1) dirender lewat
 * AssistantCanvas2dOrb; varian WebGL2 (liquid-glass-*, T5) lewat
 * AssistantOrbWebgl SELAMA WebGL2 tersedia.
 *
 * Fallback NYATA, bukan pesan error: kalau WebGL2 gagal dibuat atau hilang di
 * tengah sesi (`subscribeToWebglAvailability`), orb liquid-glass dipaksa
 * tampil sebagai "glow-ring" lewat jalur Canvas 2D yang sudah ada — varian
 * termurah, bukan layar kosong. Lihat orb-webgl-stage.ts untuk alasan kenapa
 * context loss tidak dicoba dipulihkan.
 */
export function AssistantOrb(props: AssistantOrbProps) {
	const variant = props.config.variant;
	const isWebgl = isWebglOrbVariant(variant);
	// Ketersediaan dinilai per JENIS shader: program yang gagal dikompilasi tidak
	// boleh menjatuhkan jenis lain.
	const kind = isWebgl ? webglShaderKindFor(variant) : "liquid-glass";
	const [webglAvailable, setWebglAvailable] = useState(() =>
		isWebglStageAvailable(kind),
	);

	useEffect(() => {
		if (!isWebgl) return;
		// Sinkronkan ke kondisi TERKINI saat mulai relevan — state awal di atas
		// bisa basi kalau WebGL sempat gagal sebelum komponen ini sempat mendaftar.
		setWebglAvailable(isWebglStageAvailable(kind));
		return subscribeToWebglAvailability(setWebglAvailable, kind);
	}, [isWebgl, kind]);

	if (isWebgl && webglAvailable) {
		return <AssistantOrbWebgl {...props} />;
	}
	if (isWebgl) {
		return (
			<AssistantCanvas2dOrb
				{...props}
				config={{
					...props.config,
					variant: "glow-ring",
					params: defaultOrbParams("glow-ring"),
				}}
			/>
		);
	}
	return <AssistantCanvas2dOrb {...props} />;
}
